/* ============================================================
   custom.scroll-handoff.js — Filthy Farmgirl
   30 Jul 2026

   Scroll continuity during window resizing. Two distinct problems, one file:

   1. BOTTOM-PINNING (any width).
      Fluid layouts get taller as the window widens — product images scale
      with viewport width, so between 601px and 701px a PDP gains ~1px of
      height per ~2px of width. The browser keeps scrollY CONSTANT while the
      document grows (it only clamps when you'd overshoot the end), so a
      reader parked at the bottom watches the legal bar slide off-screen.
      Measured directly: doc 3701 -> 3709 while scrollY sat frozen at 3037.
      Fix: if you were at the bottom when a resize tick fires, you stay at
      the bottom. If you were anywhere else, we don't touch you.

   2. HANDOFF ACROSS 990px.
      Horizon deliberately switches scroll containers at 990px
      (assets/base.css:35-62): below, the document scrolls; above, html/body
      get `overflow: hidden` and .page-wrapper scrolls. Stock
      scroll-container.js flips history.scrollRestoration on that media
      change and nothing else, so the incoming container is at 0 and you get
      dumped at the top of the page. Fix: carry the position across.

   Additive only — no Horizon file is patched, and nothing here changes which
   container scrolls at any width.
   ============================================================ */
(function () {
  'use strict';

  var SQUEEZE = window.matchMedia('(min-width: 990px)');
  var wrapper = document.querySelector('.page-wrapper');
  if (!wrapper) return;

  /* Within this many px of the end still counts as "at the bottom". Resize
     ticks arrive faster than scroll events update, so a tight threshold
     (1-2px) loses the pin mid-drag; 8px holds it without capturing anyone
     who has deliberately scrolled up. */
  var PIN_SLACK = 8;

  function container() {
    return SQUEEZE.matches ? wrapper : (document.scrollingElement || document.documentElement);
  }
  function getY() {
    return SQUEEZE.matches ? wrapper.scrollTop : (window.scrollY || 0);
  }
  function getMax() {
    var c = container();
    return Math.max(0, c.scrollHeight - c.clientHeight);
  }
  function setY(y) {
    /* behavior:'instant' is REQUIRED, not a nicety. base.css puts
       `scroll-behavior: smooth` on both scrollers, and a plain scrollTo
       respects it — so each pin correction animated over hundreds of ms, the
       next resize tick interrupted it, record() sampled the mid-animation
       position as "not at bottom", and the pin released itself. Stock
       scroll-container.js documents the same trap. */
    var target = SQUEEZE.matches ? wrapper : window;
    target.scrollTo({ top: y, left: 0, behavior: 'instant' });
  }

  var lastY = 0;
  var pinned = false;

  function record() {
    lastY = getY();
    pinned = lastY >= getMax() - PIN_SLACK;
  }
  record();

  // Only one of these can fire at any given width — the other container has
  // overflow:hidden and cannot scroll — so they never contend.
  window.addEventListener('scroll', record, { passive: true });
  wrapper.addEventListener('scroll', record, { passive: true });

  /* 1. Bottom-pinning. Runs on every resize tick, so the correction is
     continuous while the window is being dragged. setY triggers a scroll
     event, which re-records; we're at the bottom then, so the pin holds. */
  window.addEventListener('resize', function () {
    if (pinned) setY(getMax());
  });

  /* 2. Container handoff at 990px. Read lastY BEFORE touching the new
     container (the outgoing one has already been zeroed by overflow:hidden
     when this fires), wait a frame so the new overflow/height rules apply,
     then write. A synchronous write lands on a container that cannot scroll
     yet and is silently discarded. */
  function handoff() {
    var y = lastY;
    var wasPinned = pinned;
    requestAnimationFrame(function () {
      setY(wasPinned ? getMax() : y);
      record();
    });
  }

  // addListener fallback: older Safari, worth caring about on a store whose
  // traffic is heavily mobile.
  if (SQUEEZE.addEventListener) {
    SQUEEZE.addEventListener('change', handoff);
  } else if (SQUEEZE.addListener) {
    SQUEEZE.addListener(handoff);
  }
})();
